import { HEALTH_RECORDS_ENABLED } from '../../../../../util/healthRecords';
import React, {useEffect, useRef, useState} from 'react';
import { setSettingsDirty } from "../../../../../util/settingsDirty";
import { useTranslation } from 'react-i18next';
import BusinessComponent from "../BusinessComponent";
import {BusinessDetailsInput} from "../../../../../service/business/BusinessDetailsInput";
import { useAuth0 } from '../../../../../auth/useAuth0';
import {useBusinessContext} from "../../../../../context/BusinessContext";
import {useAlert} from "../../../../../context/alert/AlertContext";
import {BusinessDetails} from "../../../../../model/business/BusinessDetails";
import {findBusinessDetailsByBusinessId, saveBusinessDetails, findBusinessSettingByBusinessId, saveBusinessSettings} from "../../../../../service/business/business-service";
import {BusinessSettings} from "../../../../../model/business/BusinessSettings";
import {BusinessSettingsInput} from "../../../../../service/business/BusinessSettingsInput";
import {useConfirm} from "../../../../../context/confirm/ConfirmContext";
import {mapSourceToTarget} from "../../../../../util/util";
import {BusinessSummary} from "../../../../../model/business/BusinessSummary";
import ActionsButtonsComponent from "../../../../commons/action-buttons/ActionsButtonsComponent";
import { darkenHex, lightenHex } from "../../../../../util/colorMath";
import { normalizeWebsite } from "../../../../../util/links";
import { CURRENCIES } from "../../../../../util/money";
import { normalizeTimezone, timezoneOffsetLabel } from "../../../../../util/timezones";
import { SettingsField, NumberWithSuffix } from "../../../../commons/settings-row/SettingsRow";
import { formatPostal, postalPlaceholder, isCompletePostal, lookupAddress, canonicalCountry, countryToIso2 } from "../../../../../util/cep";
import { geocodeSuggest, GeoSuggestion } from "../../../../../service/geo/geo-service";
import { BR_STATES } from "../../../../../util/brStates";
import SearchableSelect from "../../../../commons/searchable-select/SearchableSelect";
import { uploadImage, mediaUrl } from "../../../../../service/media/media-service";
import { formatCnpj } from "../../../../../service/cnpj/cnpj-service";
import { validateImageFile } from "../../../../../util/imageUpload";

/**
 * The business operates across Brazil, Canada (Vancouver) and the US — expose
 * only those zones instead of the full IANA list. US covers its 4 mainland zones.
 */
// Timezones offered per country. The app only supports Brazil and Canada, so we
// never list US zones (#2). The picker is filtered by the business's own country.
const TIMEZONES_BY_COUNTRY: Record<string, string[]> = {
    brazil: [
        'America/Sao_Paulo',   // BRT (UTC-3) — most of BR
        'America/Manaus',      // AMT (UTC-4) — Amazonas / Mato Grosso
        'America/Rio_Branco',  // ACT (UTC-5) — Acre
        'America/Noronha',     // FNT (UTC-2) — Fernando de Noronha
    ],
    canada: [
        'America/St_Johns',    // NST (UTC-3:30) — Newfoundland
        'America/Halifax',     // AST (UTC-4) — Atlantic
        'America/Toronto',     // EST (UTC-5) — Eastern
        'America/Winnipeg',    // CST (UTC-6) — Central
        'America/Regina',      // CST no-DST — Saskatchewan
        'America/Edmonton',    // MST (UTC-7) — Mountain
        'America/Vancouver',   // PST (UTC-8) — Pacific
    ],
};

/** Currencies the business actually transacts in. */
const BUSINESS_CURRENCIES = ['BRL', 'CAD', 'USD'];

/**
 * Stable fingerprint of the form for the dirty check: empty string, null and
 * undefined are treated as the SAME "no value" so that typing a rejected char
 * (sanitized to '') on a field that was null doesn't register as an edit, and
 * clearing a field back to blank matches its original empty state.
 */
const fingerprint = (obj: unknown): string =>
    JSON.stringify(obj, (_k, v) => {
        // Derived coords are not a user-facing dirty signal — an address edit
        // drops them to null (re-geocoded on save), so excluding them keeps the
        // dirty check honest (edit-and-revert must clear "Salvar").
        if (_k === 'latitude' || _k === 'longitude') return undefined;
        if (v === '' || v === undefined) return null;
        // "15" (string editada) e 15 (número do backend) são o MESMO valor.
        if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
            return Number(v);
        }
        return v;
    });

const BusinessDetailsComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently, logout } = useAuth0();
    const { selectedBusiness, setSelectedBusiness, setAllBusiness, allBusiness } = useBusinessContext();
    const { setAlert } = useAlert(); // Utiliza o contexto de alertas

    const [detailsData, setDetailsData] = useState<BusinessDetails | null>(null);
    const [cepStatus, setCepStatus] = useState<'idle' | 'loading' | 'notfound'>('idle');

    // Address autocomplete (MapTiler via business-service) — same as onboarding.
    // Picking a suggestion also stamps the exact lat/long so the consumer web
    // geo filter uses the precise point; editing the address by hand clears them
    // so the backend re-geocodes the new address.
    const [addrSuggestions, setAddrSuggestions] = useState<GeoSuggestion[]>([]);
    const addrDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Dirty state derived by comparing the live form to a baseline snapshot
    // taken at load/save — NOT a boolean flipped on every keystroke. This is
    // what fixes "Salvar aparece sem eu ter mudado nada": typing a rejected
    // char (sanitized back to the same value) leaves detailsData unchanged, so
    // isModified stays false; editing a field and returning it to the original
    // value also clears the flag.
    const originalRef = useRef<string>('');
    const [isModified, setIsModified] = useState(false);
    const [websiteError, setWebsiteError] = useState(false);
    // Logo chosen but not yet uploaded — held until Save (D16). The preview is a
    // local object URL; the real R2 upload happens in handleSave.
    const [pendingImage, setPendingImage] = useState<File | null>(null);
    const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
    useEffect(() => {
        const dirty = detailsData != null
            && originalRef.current !== ''
            && (fingerprint(detailsData) !== originalRef.current || pendingImage != null);
        setIsModified(dirty);
        // QA 2026-07-04: mirror the unsaved-changes state so the Settings tab
        // shells can warn before switching away (util/settingsDirty).
        setSettingsDirty(dirty);
        return () => setSettingsDirty(false);
    }, [detailsData, pendingImage]);


    const fetchBusinessDetails = React.useCallback(async () => {
        try {
            const token = await getAccessTokenSilently();
            if (!selectedBusiness || !selectedBusiness.id) {
                setAlert('danger', t('settings.business.alerts.detailsNotLoaded'));
                return;
            }
            const fetchedBusinessDetails = await findBusinessDetailsByBusinessId(selectedBusiness?.id, token);
            if (fetchedBusinessDetails) {
                if (selectedBusiness?.id !== fetchedBusinessDetails.id){
                    await logout()
                }
                // The onboarding wizard stores ISO-2 codes ('BR'/'CA') while this
                // form + the CEP utils speak the full name ('brazil'/'canada').
                // Canonicalize on load so the select matches an option and the
                // postal mask/state field render correctly (empty stays empty).
                const canon = {
                    ...fetchedBusinessDetails,
                    country: fetchedBusinessDetails.country
                        ? canonicalCountry(fetchedBusinessDetails.country)
                        : fetchedBusinessDetails.country,
                };
                originalRef.current = fingerprint(canon); // baseline for the dirty check
                setDetailsData(canon);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            setAlert('danger', t('settings.business.alerts.detailsFetchError', { error: errorMessage }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAccessTokenSilently, logout, selectedBusiness, setAlert]);

    useEffect(() => {
        fetchBusinessDetails();
    }, [fetchBusinessDetails]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        const bizId = selectedBusiness?.id ? String(selectedBusiness.id) : null;
        if (!bizId) {
            setAlert('danger', t('settings.business.alerts.selectBeforeImage'));
            e.target.value = '';
            return;
        }
        // Validate type + size BEFORE anything (D6/D14). SVG and >5MB are rejected.
        const check = validateImageFile(file);
        if (!check.ok) {
            setAlert('danger', t(check.error === 'size'
                ? 'settings.business.alerts.imageTooLarge'
                : 'settings.business.alerts.imageBadType'));
            e.target.value = '';
            return;
        }
        // Defer the actual R2 upload to Save (D16): hold the File + a local preview.
        // Nothing hits the bucket until the owner commits — Discard leaves no orphan.
        setPendingImage(file);
        setPendingImagePreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
        // Reset so re-picking the SAME file fires onChange again (DT-08).
        e.target.value = '';
    };

    const handleClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        event.preventDefault();
        document.getElementById('businessPicture')?.click();
    };

    type BusinessDetailsField = keyof BusinessDetailsInput | `${keyof BusinessDetailsInput & string}.${string}`;

    const handleInputChange = (field: BusinessDetailsField, value: any) => {
        setDetailsData((prevData) => {
            if (!prevData) {
                // Retorna o estado original se `prevData` for `null`
                return prevData;
            }

            if (field.includes('.')) {
                const [parentKey, childKey] = field.split('.') as [keyof BusinessDetailsInput, string];

                // Verifique se `prevData[parentKey]` é um objeto válido
                const parentValue = typeof prevData[parentKey] === 'object' && prevData[parentKey] !== null
                    ? prevData[parentKey]
                    : {};

                return {
                    ...prevData,
                    [parentKey]: {
                        ...parentValue,
                        [childKey]: value,
                    },
                };
            } else {
                return {
                    ...prevData,
                    [field]: value,
                };
            }
        });
    };

    /**
     * Change a field that is part of the address fingerprint. Any manual edit
     * invalidates a previously picked point, so we drop lat/long — the backend
     * then re-geocodes the new address (or, if the address didn't actually
     * change, keeps the stored coords via the mapper's null-ignore strategy).
     */
    const handleAddressChange = (field: keyof BusinessDetailsInput, value: any) => {
        setDetailsData((prev) =>
            prev ? { ...prev, [field]: value, latitude: null, longitude: null } : prev
        );
    };

    /**
     * Switching the country wipes the whole address: a Brazilian address is
     * meaningless under Canada (and the postal mask would mangle a foreign CEP).
     * The owner re-enters via the country-appropriate search. Coords cleared too.
     */
    /**
     * Changing the business language re-labels new notification emails, but any
     * CUSTOM template the owner already wrote stays in its original language
     * (we don't machine-translate saved copy). Warn before applying (D12).
     */
    const handleLanguageChange = async (value: string) => {
        const prev = detailsData?.language;
        if (prev && value && prev !== value) {
            const ok = await confirmModal({
                title: t('common.confirmTitle'),
                message: t('settings.business.details.languageChangeWarn'),
                confirmLabel: t('common.confirm'),
            });
            if (!ok) return; // keep the old language
        }
        handleInputChange('language', value);
    };

    const handleCountryChange = async (value: string) => {
        // QA decision 2026-07-04: switching country wipes the address — confirm
        // first, via o ConfirmModal do app (nunca window.confirm).
        const hasAddress = !!(detailsData?.address1 || detailsData?.city || detailsData?.zipCode);
        if (hasAddress) {
            const ok = await confirmModal({
                title: t('common.confirmTitle'),
                message: t('settings.business.address.countryChangeConfirm'),
                danger: true,
                confirmLabel: t('common.confirm'),
            });
            if (!ok) return;
        }
        setDetailsData((prev) =>
            prev ? { ...prev, country: value, address1: '', address2: '', city: '', state: '', zipCode: '', latitude: null, longitude: null } : prev
        );
        setAddrSuggestions([]);
        setCepStatus('idle');
    };

    /** Debounced MapTiler autocomplete for the Address 1 field, biased to the
     *  selected country. Fail-soft: a miss just shows no suggestions. */
    const onAddressInput = (value: string) => {
        handleAddressChange('address1', value);
        const country = detailsData?.country;
        if (addrDebounce.current) clearTimeout(addrDebounce.current);
        if (!value || value.trim().length < 3) { setAddrSuggestions([]); return; }
        addrDebounce.current = setTimeout(async () => {
            const suggestions = await geocodeSuggest(value, countryToIso2(country));
            setAddrSuggestions(suggestions);
        }, 300);
    };

    /**
     * Fill the address from a picked suggestion and stamp the exact coordinates.
     * Presentation follows the country: Brazil keeps the "Street, number" order,
     * North America uses "number Street" — but both persist into address1 since
     * this form has a single street line.
     */
    const pickAddressSuggestion = (s: GeoSuggestion) => {
        setDetailsData((prev) => {
            if (!prev) return prev;
            const isBR = canonicalCountry(prev.country) === 'brazil';
            const line = s.houseNumber
                ? (isBR ? `${s.label}, ${s.houseNumber}` : `${s.houseNumber} ${s.label}`)
                : s.label;
            return {
                ...prev,
                address1: line || prev.address1,
                city: s.city || prev.city,
                state: s.region || prev.state,
                zipCode: s.postcode ? formatPostal(s.postcode, prev.country) : prev.zipCode,
                latitude: s.latitude ?? null,
                longitude: s.longitude ?? null,
            };
        });
        setAddrSuggestions([]);
    };

    /** On postal blur, auto-fill street/city/state — only Brazil has a lookup (ViaCEP). */
    const handleCepBlur = async () => {
        const country = detailsData?.country;
        if (canonicalCountry(country) !== 'brazil') return; // lookup is BR-only
        if (!isCompletePostal(detailsData?.zipCode, country)) { setCepStatus('idle'); return; }
        setCepStatus('loading');
        const found = await lookupAddress(detailsData?.zipCode, country);
        if (found) {
            if (found.street) handleInputChange('address1', found.street);
            if (found.city) handleInputChange('city', found.city);
            if (found.state) handleInputChange('state', found.state);
            setCepStatus('idle');
        } else {
            setCepStatus('notfound');
        }
    };

    const handleSave = async () => {
        if (detailsData) {
            // Guard: the mount business must match — otherwise stop (never save
            // to the wrong business after logging out).
            if (selectedBusiness?.id !== detailsData.id) {
                await logout();
                return;
            }
            // Name is required and can't be only spaces.
            if (!(detailsData.name ?? '').trim()) {
                setAlert('danger', t('settings.business.details.nameRequired'));
                return;
            }
            // Website must be a valid link (empty is fine).
            const web = normalizeWebsite(detailsData.website);
            if (!web.ok) {
                setWebsiteError(true);
                setAlert('danger', t('settings.business.details.websiteInvalid'));
                return;
            }
            try {
                const token = await getAccessTokenSilently();

                // Upload the deferred logo now (D16) — only on Save. On failure,
                // abort the whole save so the owner keeps their unsaved changes.
                let imageKey = detailsData.image;
                if (pendingImage) {
                    try {
                        imageKey = await uploadImage(pendingImage, `business/${detailsData.id}`, token);
                    } catch (err) {
                        setAlert('danger', err instanceof Error ? err.message : t('settings.business.alerts.imageUploadError'));
                        return;
                    }
                }

                const input: BusinessDetailsInput = mapSourceToTarget(
                    { ...detailsData, name: detailsData.name.trim(), website: web.value, image: imageKey,
                      // Normalize to a valid IANA zone so a legacy/malformed stored
                      // value (label or spaced) can't fail the backend save (#3).
                      timezone: normalizeTimezone(detailsData.timezone) }, BusinessDetailsInput);

                const result = await saveBusinessDetails(input, token);

                if (result.success) {
                    // Fold the uploaded key into the form + clear the pending file
                    // so the baseline (and preview) reflect the stored image.
                    const savedData = { ...detailsData, image: imageKey };
                    setDetailsData(savedData);
                    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
                    setPendingImage(null);
                    setPendingImagePreview(null);
                    // The saved form IS the new clean baseline — update the
                    // snapshot so the dirty check goes back to false.
                    originalRef.current = fingerprint(savedData);
                    setIsModified(false);

                    const updatedBusiness = {
                        ...selectedBusiness,
                        id: selectedBusiness?.id,
                        name: input.name,
                        image: input.image,
                        brandColor: input.brandColor,
                        brandColorStrong: input.brandColorStrong,
                        brandColorSoft: input.brandColorSoft,
                        active: selectedBusiness?.active,
                    };

                    setSelectedBusiness(updatedBusiness);

                    const updatedAllBusiness = allBusiness?.map((business: BusinessSummary) =>
                        business.id === updatedBusiness.id ? updatedBusiness : business
                    ) || null;

                    setAllBusiness(updatedAllBusiness);

                    setAlert('success', t('settings.business.details.saveSuccess'));
                } else {
                    setAlert('danger', t('settings.business.details.saveError', { error: result.error ?? '' }));
                }
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                setAlert('danger', t('settings.business.alerts.detailsSaveError', { error: errorMessage }));
            }
        }
    };

    // Descartar restaura o estado ORIGINAL (recarrega do backend), igual às
    // seções Calendário/Notificações — antes só limpava a flag e as edições
    // continuavam na tela.
    const handleCancel = () => {
        setIsModified(false);
        setAddrSuggestions([]);
        setCepStatus('idle');
        // Drop the deferred logo (never uploaded → nothing to clean up) and its preview.
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
        setPendingImage(null);
        setPendingImagePreview(null);
        fetchBusinessDetails();
    };

    // ---- Prontuário master switch (health mode) — saved immediately, apart from
    // the Details form. Turning OFF asks for a conscious confirmation (friction
    // only in the dangerous direction).
    const confirmModal = useConfirm();
    const [healthSettings, setHealthSettings] = useState<BusinessSettings | null>(null);
    const [healthSaving, setHealthSaving] = useState(false);
    useEffect(() => {
        if (!selectedBusiness?.id) return;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const st = await findBusinessSettingByBusinessId(Number(selectedBusiness.id), token);
                setHealthSettings(st);
            } catch { /* section simply hides on failure */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBusiness?.id]);

    const handleHealthModeChange = async (value: boolean) => {
        if (!healthSettings) return;
        if (!value) {
            const ok = await confirmModal({
                title: t('settings.business.health.disableTitle'),
                message: t('settings.business.health.disableWarning'),
                danger: true,
                confirmLabel: t('common.confirm'),
            });
            if (!ok) return;
        }
        try {
            setHealthSaving(true);
            const token = await getAccessTokenSilently();
            const input: BusinessSettingsInput = mapSourceToTarget({ ...healthSettings, healthMode: value }, BusinessSettingsInput);
            const result = await saveBusinessSettings(input, token);
            if ((result as any)?.success === false) {
                setAlert('danger', t('settings.business.health.saveError'));
                return;
            }
            setHealthSettings({ ...healthSettings, healthMode: value });
            setAlert('success', value
                ? t('settings.business.health.enabledMsg')
                : t('settings.business.health.disabledMsg'));
        } catch (e) {
            setAlert('danger', t('settings.business.health.saveError'));
        } finally {
            setHealthSaving(false);
        }
    };

    return (
        <div className="side-content-vertical">
            <ActionsButtonsComponent
                isModified={isModified}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="profile-section-container separated-container">
                <h3 className="section-header">{t('settings.business.details.sectionTitle')}</h3>
                <form className="busniness-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="businessPicture">{t('settings.business.details.businessPicture')}</label>
                            <input
                                type="file"
                                id="businessPicture"
                                data-cy="biz-picture-input"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                onChange={handleImageChange}
                                style={{display: 'none'}}
                            />
                            <img
                                src={pendingImagePreview || mediaUrl(detailsData?.image) || 'default-profile.png'}
                                alt="Business Logo"
                                className="profile-picture"
                                onClick={handleClick}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="language">{t('settings.business.details.language')}</label>
                            <select
                                id="language"
                                data-cy="biz-language-select"
                                value={detailsData?.language || 'English'}
                                onChange={(e) => handleLanguageChange(e.target.value)}
                            >
                                <option value="English">{t('settings.business.details.languageEnglish')}</option>
                                <option value="Portuguese">{t('settings.business.details.languagePortuguese')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="timeFormat">{t('settings.business.details.timeFormat')}</label>
                            <select
                                id="timeFormat"
                                data-cy="biz-timeformat-select"
                                value={detailsData?.timeFormat || 'H24'}
                                onChange={(e) => handleInputChange('timeFormat', e.target.value)}
                            >
                                <option value="H24">{t('settings.business.details.timeFormatH24')}</option>
                                <option value="AMPM">{t('settings.business.details.timeFormatAmpm')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="timezone">{t('settings.business.details.timezone')}</label>
                            {/* Type-to-filter — the IANA list has hundreds of entries.
                             * Legacy BRT/AMT/etc. normalize to IANA on display. */}
                            <SearchableSelect
                                inputId="timezone"
                                dataCy="biz-timezone-select"
                                value={normalizeTimezone(detailsData?.timezone)}
                                onChange={(v) => handleInputChange('timezone', v)}
                                placeholder={t('settings.business.details.timezonePlaceholder')}
                                options={(() => {
                                    // Only the business's own country's zones (BR or CA);
                                    // default to Brazil when country is unset (BR-primary app).
                                    const countryZones =
                                        TIMEZONES_BY_COUNTRY[canonicalCountry(detailsData?.country)]
                                        ?? TIMEZONES_BY_COUNTRY.brazil;
                                    // Always include the stored zone so the select never renders
                                    // blank on a legacy value outside the shortlist (DT-09).
                                    const cur = normalizeTimezone(detailsData?.timezone);
                                    const zones = cur && !countryZones.includes(cur)
                                        ? [cur, ...countryZones]
                                        : countryZones;
                                    return zones.map((tz) => ({
                                        value: tz,
                                        label: `(${timezoneOffsetLabel(tz)}) ${tz.replace(/_/g, ' ')}`,
                                    }));
                                })()}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="currency">{t('settings.business.details.currency')}</label>
                            {/* Moeda é IMUTÁVEL após o cadastro (decisão 2026-07-04):
                                trocar deixaria as transações já lançadas na moeda antiga,
                                misturando o histórico. Quem precisa de outra moeda cadastra
                                outra empresa. Normalize legacy "REAL" → "BRL". */}
                            <SearchableSelect
                                inputId="currency"
                                dataCy="biz-currency-select"
                                value={detailsData?.currency === 'REAL' ? 'BRL' : detailsData?.currency || 'USD'}
                                onChange={(v) => handleInputChange('currency', v)}
                                isDisabled
                                placeholder={t('settings.business.details.currencyPlaceholder')}
                                options={CURRENCIES.filter((c) =>
                                    BUSINESS_CURRENCIES.includes(c.code)
                                ).map((c) => ({
                                    value: c.code,
                                    label: `${c.code} — ${t('currency.' + c.code, c.code)} (${c.symbol})`,
                                }))}
                            />
                            <small className="form-text text-muted">
                                {t('settings.business.details.currencyLockedHint')}
                            </small>
                        </div>
                        <div className="form-group">
                            <label htmlFor="name">{t('settings.business.details.businessName')}</label>
                            <input
                                type="text"
                                id="name"
                                data-cy="biz-name-input"
                                maxLength={255}
                                value={detailsData?.name || ''}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="website">{t('settings.business.details.website')}</label>
                            <input
                                type="text"
                                id="website"
                                data-cy="biz-website-input"
                                maxLength={255}
                                value={detailsData?.website || ''}
                                placeholder={t('settings.business.details.websitePlaceholder')}
                                onChange={(e) => { handleInputChange('website', e.target.value); setWebsiteError(false); }}
                                onBlur={(e) => {
                                    const r = normalizeWebsite(e.target.value);
                                    if (r.ok) { setWebsiteError(false); if (r.value !== (detailsData?.website || '')) handleInputChange('website', r.value ?? ''); }
                                    else setWebsiteError(true);
                                }}
                            />
                            {websiteError && (
                                <small className="form-text text-danger" data-cy="biz-website-error">
                                    {t('settings.business.details.websiteInvalid')}
                                </small>
                            )}
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group" style={{ flexBasis: '100%' }}>
                            <label>{t('settings.business.details.brandColors')}</label>
                            {(() => {
                                const main = detailsData?.brandColor || '#7266E8';
                                const strong =
                                    detailsData?.brandColorStrong || darkenHex(main);
                                const soft =
                                    detailsData?.brandColorSoft || lightenHex(main);

                                /** When the user picks a new Main, auto-update Strong/Soft
                                 *  if they still hold the auto-derived values from the
                                 *  previous Main (i.e. were never customized). */
                                const onMainChange = (next: string) => {
                                    const prevDerivedStrong = darkenHex(main).toUpperCase();
                                    const prevDerivedSoft = lightenHex(main).toUpperCase();
                                    const curStrong = (
                                        detailsData?.brandColorStrong || prevDerivedStrong
                                    ).toUpperCase();
                                    const curSoft = (
                                        detailsData?.brandColorSoft || prevDerivedSoft
                                    ).toUpperCase();

                                    handleInputChange('brandColor', next.toUpperCase());
                                    if (curStrong === prevDerivedStrong) {
                                        handleInputChange(
                                            'brandColorStrong',
                                            darkenHex(next).toUpperCase()
                                        );
                                    }
                                    if (curSoft === prevDerivedSoft) {
                                        handleInputChange(
                                            'brandColorSoft',
                                            lightenHex(next).toUpperCase()
                                        );
                                    }
                                };

                                const swatchInput = (
                                    value: string,
                                    onChange: (hex: string) => void
                                ) => (
                                    <label
                                        style={{
                                            position: 'relative',
                                            width: 42,
                                            height: 42,
                                            borderRadius: 10,
                                            border: '1px solid #d8d8e2',
                                            cursor: 'pointer',
                                            background: value,
                                            display: 'inline-block',
                                            boxShadow:
                                                '0 1px 3px rgba(20,20,60,0.08), inset 0 0 0 2px #fff',
                                        }}
                                    >
                                        <input
                                            type="color"
                                            value={value}
                                            onChange={(e) => onChange(e.target.value)}
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                opacity: 0,
                                                cursor: 'pointer',
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        />
                                    </label>
                                );

                                const cell = (
                                    title: string,
                                    hint: string,
                                    value: string,
                                    onChange: (hex: string) => void
                                ) => (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '0.35rem',
                                            minWidth: 130,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            {swatchInput(value, onChange)}
                                            <div>
                                                <div
                                                    style={{
                                                        fontSize: '0.82rem',
                                                        fontWeight: 600,
                                                        color: '#333',
                                                    }}
                                                >
                                                    {title}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: '0.72rem',
                                                        color: '#888',
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {hint}
                                                </div>
                                            </div>
                                        </div>
                                        <code
                                            style={{
                                                fontSize: '0.72rem',
                                                color: 'var(--gs-text-muted, #666)',
                                                background: 'var(--gs-surface-soft, #f5f5fa)',
                                                padding: '0.1rem 0.4rem',
                                                borderRadius: 4,
                                            }}
                                        >
                                            {value.toUpperCase()}
                                        </code>
                                    </div>
                                );

                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '1.25rem',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        {cell(
                                            t('settings.business.details.brandColorsMain'),
                                            t('settings.business.details.brandColorsMainHint'),
                                            main,
                                            onMainChange
                                        )}
                                        {cell(
                                            t('settings.business.details.brandColorsStrong'),
                                            t('settings.business.details.brandColorsStrongHint'),
                                            strong,
                                            (next) =>
                                                handleInputChange(
                                                    'brandColorStrong',
                                                    next.toUpperCase()
                                                )
                                        )}
                                        {cell(
                                            t('settings.business.details.brandColorsSoft'),
                                            t('settings.business.details.brandColorsSoftHint'),
                                            soft,
                                            (next) =>
                                                handleInputChange(
                                                    'brandColorSoft',
                                                    next.toUpperCase()
                                                )
                                        )}
                                    </div>
                                );
                            })()}
                            <div
                                style={{
                                    fontSize: '0.72rem',
                                    color: '#888',
                                    marginTop: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span>{t('settings.business.details.brandColorsHint')}</span>
                                {(detailsData?.brandColor ||
                                    detailsData?.brandColorStrong ||
                                    detailsData?.brandColorSoft) && (
                                    <button
                                        type="button"
                                        data-cy="biz-brand-color-reset-btn"
                                        onClick={() => {
                                            handleInputChange('brandColor', null);
                                            handleInputChange('brandColorStrong', null);
                                            handleInputChange('brandColorSoft', null);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid #e2e2ea',
                                            borderRadius: 999,
                                            padding: '0.2rem 0.7rem',
                                            fontSize: '0.72rem',
                                            color: '#555',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {t('settings.business.details.brandColorsReset')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* "Último acesso" moved to the user's Profile (D10): it is the
                       logged-in USER's last login, not a business attribute, and
                       belongs with the account, in the business locale/timezone. */}
                </form>
            </div>

            {/* CNPJ / Receita — read-only (D9). Only for businesses onboarded via
               CNPJ; manual/autônomo businesses have no CNPJ and this stays hidden.
               These fields are set once at onboarding and are not editable here. */}
            {detailsData?.cnpj && (
                <div className="profile-section-container separated-container" data-cy="biz-cnpj-section">
                    <h3 className="section-header">{t('settings.business.cnpj.sectionTitle')}</h3>
                    <form className="busniness-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="cnpj">{t('settings.business.cnpj.cnpj')}</label>
                                <input type="text" id="cnpj" data-cy="biz-cnpj"
                                    value={formatCnpj((detailsData.cnpj || '').replace(/\D/g, ''))}
                                    disabled readOnly />
                            </div>
                            {detailsData.legalName && (
                                <div className="form-group">
                                    <label htmlFor="legalName">{t('settings.business.cnpj.legalName')}</label>
                                    <input type="text" id="legalName" data-cy="biz-legal-name"
                                        value={detailsData.legalName} disabled readOnly />
                                </div>
                            )}
                        </div>
                        {(detailsData.cnae || detailsData.registeredEmail || detailsData.registeredPhone) && (
                            <div className="form-row">
                                {detailsData.cnae && (
                                    <div className="form-group">
                                        <label htmlFor="cnae">{t('settings.business.cnpj.cnae')}</label>
                                        <input type="text" id="cnae" data-cy="biz-cnae"
                                            value={detailsData.cnae} disabled readOnly />
                                    </div>
                                )}
                                {detailsData.registeredEmail && (
                                    <div className="form-group">
                                        <label htmlFor="registeredEmail">{t('settings.business.cnpj.registeredEmail')}</label>
                                        <input type="text" id="registeredEmail" data-cy="biz-registered-email"
                                            value={detailsData.registeredEmail} disabled readOnly />
                                    </div>
                                )}
                            </div>
                        )}
                        <small className="text-muted d-block mt-1">{t('settings.business.cnpj.hint')}</small>
                    </form>
                </div>
            )}

            {/* Prontuário off: hide the switch. The one exception is a business that
                somehow still has it ON, which keeps the card so the owner can turn
                it OFF — the backend always accepts false, never true. */}
            {healthSettings && (HEALTH_RECORDS_ENABLED || !!healthSettings.healthMode) && (
                <div className="profile-section-container separated-container" data-cy="health-mode-section">
                    <h3 className="section-header">{t('settings.business.health.sectionTitle')}</h3>
                    <label className="gs-inline-check">
                        <input
                            type="checkbox"
                            className="gs-inline-check__box"
                            checked={!!healthSettings.healthMode}
                            disabled={healthSaving}
                            onChange={(e) => handleHealthModeChange(e.target.checked)}
                            data-cy="health-mode-toggle"
                        />
                        <span className="gs-inline-check__label">{t('settings.business.health.toggleLabel')}</span>
                    </label>
                    <small className="text-muted d-block mt-1">{t('settings.business.health.toggleHint')}</small>
                </div>
            )}

            {/* Rate Section — all rates are percentages, rendered with the
                same suffix-chip input used elsewhere in Settings. */}
            <div className="rate-section-container separated-container">
                <h3 className="section-header">{t('settings.business.rates.sectionTitle')}</h3>
                <form className="rates-form">
                    <div className="gs-settings-grid">
                        {/* Platform fee + service fee removed — this is a SaaS (no platform
                            cut), and "service fee" was really derived profit, not an input.
                            Only the estimated tax rate stays (the company default; each
                            service can override it). */}
                        <SettingsField label={t('settings.business.rates.retailsSalesTaxRate')} htmlFor="retailsSalesTaxRate">
                            <NumberWithSuffix
                                id="retailsSalesTaxRate"
                                value={detailsData?.retailsSalesTaxRate ?? ''}
                                onChange={(v) => handleInputChange('retailsSalesTaxRate', v)}
                                suffix="%"
                                max={100}
                                placeholder="0"
                            />
                            <small className="form-text text-muted">
                                {t('settings.business.rates.taxRateHint')}
                            </small>
                        </SettingsField>
                    </div>
                </form>
            </div>

            {/* Address Section */}
            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('settings.business.address.sectionTitle')}</h3>
                <form className="address-form">
                    {/* Presentation follows the country, like the onboarding wizard:
                        Brazil leads with the CEP (auto-fills street/city/state); other
                        countries lead with the address search. Both use the same MapTiler
                        autocomplete, which stamps the exact lat/long on pick. */}
                    {(() => {
                        const isBR = canonicalCountry(detailsData?.country) === 'brazil';

                        const countryField = (
                            <div className="form-group">
                                <label htmlFor="country">{t('settings.business.address.country')}</label>
                                {/* País é IMUTÁVEL após o cadastro (decisão 2026-07-04):
                                    define máscara de CEP/telefone e formato de endereço já
                                    aplicados aos dados existentes. Quem precisa de outro
                                    país cadastra outra empresa. */}
                                <select id="country" data-cy="biz-country-select" value={detailsData?.country || ''}
                                        disabled
                                        onChange={(e) => handleCountryChange(e.target.value)}>
                                    <option value="brazil">{t('settings.business.address.countryBrazil')}</option>
                                    <option value="canada">{t('settings.business.address.countryCanada')}</option>
                                </select>
                                <small className="form-text text-muted">
                                    {t('settings.business.address.countryLockedHint')}
                                </small>
                            </div>
                        );

                        const zipField = (
                            <div className="form-group">
                                <label htmlFor="zipCode">{isBR ? t('settings.business.address.zipCode') : t('settings.business.address.postalCode')}</label>
                                <input type="text" id="zipCode" data-cy="biz-zipcode-input" inputMode="numeric" maxLength={20}
                                       value={formatPostal(detailsData?.zipCode, detailsData?.country)}
                                       onChange={(e) => { handleAddressChange('zipCode', formatPostal(e.target.value, detailsData?.country)); setCepStatus('idle'); }}
                                       onBlur={handleCepBlur}
                                       placeholder={postalPlaceholder(detailsData?.country)}/>
                                {cepStatus === 'loading' && <span className="cep-hint">{t('settings.business.address.cepSearching')}</span>}
                                {cepStatus === 'notfound' && <span className="cep-hint cep-hint--err">{t('settings.business.address.cepNotFound')}</span>}
                            </div>
                        );

                        const cityField = (
                            <div className="form-group">
                                <label htmlFor="city">{t('settings.business.address.city')}</label>
                                {/* Brazil: a cidade é DERIVADA do CEP (ViaCEP) e fica travada
                                    (read-only) — sem isso o usuário digitava lixo como "uygug".
                                    Outros países não têm lookup, então lá é texto livre (só tira
                                    dígitos: nome de cidade não tem número). */}
                                <input type="text" id="city" data-cy="biz-city-input" maxLength={255}
                                       value={detailsData?.city || ''}
                                       readOnly={isBR}
                                       onChange={(e) => handleAddressChange('city', e.target.value.replace(/[0-9]/g, ''))}
                                       placeholder={isBR ? t('settings.business.address.cityFromCep') : t('settings.business.address.cityPlaceholder')}/>
                                {isBR && (
                                    <small className="form-text text-muted">
                                        {t('settings.business.address.cityFromCepHint')}
                                    </small>
                                )}
                            </div>
                        );

                        const addressField = (
                            <div className="form-group">
                                <label htmlFor="biz-address1">{t('settings.business.address.address1')}</label>
                                <div className="gs-ac">
                                    {/* id/name deliberately non-"address" + autofill off so Chrome's
                                        saved-address popup doesn't cover our own suggestion list. */}
                                    <input type="text" id="biz-address1" name="biz-address1" data-cy="biz-address1-input" maxLength={255}
                                           autoComplete="off" data-lpignore="true" data-1p-ignore
                                           value={detailsData?.address1 || ''}
                                           onChange={(e) => onAddressInput(e.target.value)}
                                           onBlur={() => setTimeout(() => setAddrSuggestions([]), 150)}
                                           placeholder={t('settings.business.address.address1Placeholder')}/>
                                    {addrSuggestions.length > 0 && (
                                        <ul className="gs-ac__list" data-cy="biz-address-suggestions">
                                            {addrSuggestions.map((s, i) => (
                                                <li key={i} className="gs-ac__item" onMouseDown={() => pickAddressSuggestion(s)}>
                                                    <span className="gs-ac__label">{s.label}</span>
                                                    {s.context && <span className="gs-ac__ctx">{s.context}</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        );

                        const address2Field = (
                            <div className="form-group">
                                <label htmlFor="apartment">{t('settings.business.address.address2')}</label>
                                <input type="text" id="apartment" data-cy="biz-address2-input" maxLength={255} value={detailsData?.address2 || ''}
                                       onChange={(e) => handleAddressChange('address2', e.target.value)}
                                       placeholder={t('settings.business.address.address2Placeholder')}/>
                            </div>
                        );

                        const stateField = (
                            <div className="form-group">
                                <label htmlFor="state">{t('settings.business.address.state')}</label>
                                {/* Brazil → UF dropdown (no free-typing junk/numbers). Other
                                    countries keep a free-text field. */}
                                {isBR ? (
                                    <>
                                        {/* Brasil: a UF também é DERIVADA do CEP (ViaCEP retorna a uf).
                                            Editável, o usuário conseguia salvar CEP da Bahia com estado
                                            GO — inconsistente. Travado igual à cidade. */}
                                        <select id="state" data-cy="biz-state-select" value={detailsData?.state || ''}
                                                disabled
                                                onChange={(e) => handleAddressChange('state', e.target.value)}>
                                            <option value="">{t('settings.business.address.statePlaceholder')}</option>
                                            {BR_STATES.map((s) => (
                                                <option key={s.uf} value={s.uf}>{s.uf} — {s.name}</option>
                                            ))}
                                        </select>
                                        <small className="form-text text-muted">
                                            {t('settings.business.address.stateFromCepHint')}
                                        </small>
                                    </>
                                ) : (
                                    <input type="text" id="state" data-cy="biz-state-input" maxLength={255} value={detailsData?.state || ''}
                                           onChange={(e) => handleAddressChange('state', e.target.value)}
                                           placeholder={t('settings.business.address.statePlaceholder')}/>
                                )}
                            </div>
                        );

                        return isBR ? (
                            <>
                                <div className="form-row">{countryField}{zipField}{cityField}</div>
                                <div className="form-row">{addressField}{address2Field}{stateField}</div>
                            </>
                        ) : (
                            <>
                                <div className="form-row">{countryField}{addressField}{address2Field}</div>
                                <div className="form-row">{cityField}{stateField}{zipField}</div>
                            </>
                        );
                    })()}
                </form>
            </div>
        </div>
    );
};

export default BusinessDetailsComponent;
